import React, { useState, useEffect, useCallback, useMemo } from 'react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import Swal from 'sweetalert2'
import { addSessionToken, isTokenValid } from 'src/util/auth'
import { API_URLS } from 'src/util/apiUrls'
import {
  CButton,
  CCol,
  CContainer,
  CForm,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CRow,
  CSpinner,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilLockLocked, cilUser } from '@coreui/icons'
import { encryptPassword } from 'src/util/encryption'
import { generateMathCaptcha, MathCaptcha } from 'src/util/generateCaptcha'

const Login = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [userCaptcha, setUserCaptcha] = useState('')
  const [captcha, setCaptcha] = useState(generateMathCaptcha())
  const [isSubmitting, setIsSubmitting] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  useEffect(() => {
    if (isTokenValid()) {
      navigate('/dashboard')
    }
  }, [navigate])

  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateMathCaptcha())
    setUserCaptcha('')
  }, [])

  const handleLogin = useCallback(
    async (e) => {
      e.preventDefault()

      if (!email || !password) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Email and password are required.',
        })
        return
      }

      // Validate Math CAPTCHA
      if (parseInt(userCaptcha) !== captcha.answer) {
        Swal.fire({
          icon: 'error',
          title: 'CAPTCHA Failed',
          text: 'Incorrect answer. Please try again.',
        })
        refreshCaptcha()
        return
      }

      setIsSubmitting(true)

      try {
        const saltResponse = await axios.post(API_URLS.GET_SALT, { Email: email })
        if (!saltResponse.data.IsSuccess) {
          refreshCaptcha() // Refresh captcha on salt retrieval failure
          throw new Error('Failed to retrieve encryption salt')
        }

        const salt = saltResponse.data.Response.SaltForEncryption
        const encryptedPassword = encryptPassword(password, salt)

        const loginData = {
          Email: email,
          Password: encryptedPassword,
          Salt: salt,
        }

        const response = await axios.post(API_URLS.LOGIN, loginData)

        if (response?.data?.IsSuccess && response?.data?.Response?.Token) {
          const { Token, TokenRefresh, TokenExpiresIn, UserId, FirstName, LastName, RoleName } =
            response.data.Response

          addSessionToken(Token, TokenRefresh, TokenExpiresIn)
          dispatch({
            type: 'SET_USER',
            user: {
              userId: UserId,
              name: `${FirstName} ${LastName}`,
              role: RoleName || 'User',
            },
          })

          navigate('/dashboard')
        } else {
          refreshCaptcha() // Refresh captcha on login failure
          throw new Error(response?.data?.Message || 'Invalid email or password.')
        }
      } catch (error) {
        refreshCaptcha() // Refresh captcha on any error
        Swal.fire({
          icon: 'error',
          title: 'Login Failed',
          text: error.message || 'An unexpected error occurred.',
        })
      } finally {
        setIsSubmitting(false)
      }
    },
    [email, password, userCaptcha, captcha, navigate, dispatch, refreshCaptcha],
  )

  const isFormValid = useMemo(() => {
    return email && password && userCaptcha
  }, [email, password, userCaptcha])

  return (
    <div className="min-vh-100 d-flex flex-row align-items-center loginPage">
      <CContainer fluid>
        <CRow className="justify-content-center">
          <CCol xs={11} md={6} lg={4} xl={4} xxl={3} className="loginFrom">
            <CForm onSubmit={handleLogin}>
              <h3 className="mb-0 text-center">Admin Login</h3>
              <p className="text-center mb-3">Sign In to your account</p>
              <CInputGroup className="mb-3">
                <CInputGroupText>
                  <CIcon icon={cilUser} />
                </CInputGroupText>
                <CFormInput
                  placeholder="Email"
                  autoComplete="off"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </CInputGroup>
              <CInputGroup className="mb-4">
                <CInputGroupText>
                  <CIcon icon={cilLockLocked} />
                </CInputGroupText>
                <CFormInput
                  type="password"
                  placeholder="Password"
                  autoComplete="off"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </CInputGroup>

              {/* Math CAPTCHA Component */}
              <MathCaptcha
                captcha={captcha}
                userCaptcha={userCaptcha}
                setUserCaptcha={setUserCaptcha}
                refreshCaptcha={refreshCaptcha}
              />

              <CRow className="mb-3">
                <CCol>
                  <CButton
                    className="w-100 login_primary"
                    type="submit"
                    disabled={isSubmitting || !isFormValid}
                  >
                    {isSubmitting ? <CSpinner size="sm" /> : 'Login'}
                  </CButton>
                </CCol>
                <CCol>
                  <CButton
                    color="link"
                    className="w-100 text-decoration-none"
                    onClick={() => navigate('/forgetpassword')}
                  >
                    Forgot password?
                  </CButton>
                </CCol>
              </CRow>
            </CForm>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default Login
